import { useRef, useState } from 'react';
import { INSIGHT_TELEMETRY_BOARD, trackInsight } from '@/actionBoards/actionBoard.telemetry';
import { sqWorkbenchStore, sqWorkbookStore } from '@/core/core.stores';
import { useFluxPaths } from '@/core/hooks/useFluxPaths.hook';
import { addWorkbook } from '@/homescreen/homescreen.actions';
import { EMPTY_GUID, HOME_SCREEN_TABS } from '@/main/app.constants';
import { getWorksheetLink } from '@/main/routing.utilities';
import { canReadItem, isTrustedUser } from '@/services/authorization.service';
import { TRACK } from '@/track/track.constants';
import { doTrack } from '@/track/track.service';
import { createUniqueCopyName, getTabFolder } from '@/utilities/homescreen.utilities';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { SeeqNames } from '@seeqdev/constants';
import type { StoreWorkbookWithWorksheets } from '@/worksheet/worksheet.types';

const supportsDuplication = (type: string | undefined): boolean =>
  type === SeeqNames.Types.Analysis || type === SeeqNames.Types.Topic || type === SeeqNames.Types.ActionBoard;

/** Copies the current workbook without leaving it. */
export const useDuplicateWorkbook = (): {
  workbookType: string | undefined;
  canDuplicate: boolean;
  isDuplicating: boolean;
  duplicateWorkbook: () => Promise<void>;
} => {
  const { workbookId, type, effectivePermissions } = useFluxPaths(sqWorkbookStore, [
    'workbookId',
    'type',
    'effectivePermissions',
  ]);
  const { currentUser, stateParams } = useFluxPaths(sqWorkbenchStore, ['currentUser', 'stateParams']);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const duplicateInProgress = useRef(false);
  const canDuplicate =
    Boolean(workbookId) &&
    workbookId === stateParams.workbookId &&
    supportsDuplication(type) &&
    currentUser.isTrusted &&
    canReadItem({ effectivePermissions });

  const duplicateWorkbook = async (): Promise<void> => {
    if (
      duplicateInProgress.current ||
      !canDuplicate ||
      sqWorkbookStore.workbookId !== workbookId ||
      sqWorkbenchStore.stateParams.workbookId !== workbookId ||
      !isTrustedUser() ||
      !canReadItem(sqWorkbookStore)
    ) {
      return;
    }

    const { name, parentFolderId, type: sourceType, worksheets } = sqWorkbookStore;
    const worksheetName = worksheets.find(
      (worksheet) => worksheet.worksheetId === sqWorkbenchStore.stateParams.worksheetId,
    )?.name;
    const hasUniqueWorksheetName =
      worksheetName !== undefined && worksheets.filter((worksheet) => worksheet.name === worksheetName).length === 1;
    duplicateInProgress.current = true;
    setIsDuplicating(true);
    if (sourceType === SeeqNames.Types.ActionBoard) {
      trackInsight({ category: INSIGHT_TELEMETRY_BOARD, action: 'Duplicate Clicked' });
    } else {
      doTrack(sourceType === SeeqNames.Types.Topic ? TRACK.TOPIC : TRACK.ANALYSIS, 'Duplicate Workbook', {
        workbookId,
      });
    }

    try {
      let folderId = parentFolderId;
      if (!folderId || folderId === EMPTY_GUID) {
        const myFolder: { id: string } | undefined = await getTabFolder(HOME_SCREEN_TABS.MY_FOLDER);
        if (!myFolder?.id || myFolder.id === EMPTY_GUID) {
          errorToast({ messageKey: 'HOME_SCREEN.MY_FOLDER_UNAVAILABLE' });
          return;
        }
        folderId = myFolder.id;
      }

      const copy: StoreWorkbookWithWorksheets = await addWorkbook({
        name: createUniqueCopyName(name),
        branchFrom: workbookId,
        folderId,
        addNewWorksheet: false,
        isTopic: sourceType === SeeqNames.Types.Topic,
        isActionBoard: sourceType === SeeqNames.Types.ActionBoard,
      });
      // Duplication can omit worksheets or prepend an error report, so positions do not identify the copy.
      const matchingWorksheets = hasUniqueWorksheetName
        ? copy.worksheets?.filter((worksheet) => worksheet.name === worksheetName)
        : undefined;
      const copyLink = getWorksheetLink(
        copy.id,
        matchingWorksheets?.length === 1 ? matchingWorksheets[0].worksheetId : undefined,
      );
      successToast({
        messageKey: 'HOME_SCREEN.DUPLICATION_SUCCESS',
        messageParams: { ITEM_NAME: copy.name },
        buttonLabelKey: 'OPEN',
        buttonAction: () => window.open(copyLink, '_blank', 'noopener'),
        buttonIcon: null,
      });
    } catch (error) {
      errorToast({ httpResponseOrError: error, displayForbidden: true });
    } finally {
      duplicateInProgress.current = false;
      setIsDuplicating(false);
    }
  };

  return { workbookType: type, canDuplicate, isDuplicating, duplicateWorkbook };
};
